/**
 * Module/Feature Guard components
 *
 * <ModuleGuard module="talent">     — blocks rendering if module not active
 * <FeatureGuard feature="vagas">    — blocks rendering if feature not allowed
 * <EditGuard feature="vagas">       — blocks rendering if user can't edit
 */

import { ReactNode } from "react";
import { useModuleAccess, type ModuleKey } from "@/modules/shared/hooks/useModuleAccess";
import { ModuleNotAvailable } from "./ModuleNotAvailable";

// ─── Pure guard logic (extracted for testability) ───────────────────────────

export type GuardResult = "loading" | "allowed" | "blocked";

/**
 * Determines whether a route guard should allow or block access to a module.
 *
 * @param module - The module key the route requires
 * @param userModules - Set of modules the user has access to
 * @param loading - Whether module access data is still loading
 * @returns "loading" | "allowed" | "blocked"
 */
export function resolveModuleGuard(
  module: ModuleKey,
  userModules: Set<ModuleKey>,
  loading: boolean,
): GuardResult {
  if (loading) return "loading";
  if (!userModules.has(module)) return "blocked";
  return "allowed";
}

// ─── Components ─────────────────────────────────────────────────────────────

interface ModuleGuardProps {
  module: ModuleKey;
  children: ReactNode;
  /** Custom fallback to render when access is denied (default: ModuleNotAvailable screen) */
  fallback?: ReactNode;
}

export function ModuleGuard({ module, children, fallback }: ModuleGuardProps) {
  const { hasModule, loading } = useModuleAccess();

  if (loading) {
    return null; // Could show a spinner if desired
  }

  if (!hasModule(module)) {
    return <>{fallback ?? <ModuleNotAvailable module={module} />}</>;
  }

  return <>{children}</>;
}

interface FeatureGuardProps {
  feature: string;
  children: ReactNode;
  /** Custom fallback (default: render nothing) */
  fallback?: ReactNode;
}

export function FeatureGuard({ feature, children, fallback = null }: FeatureGuardProps) {
  const { hasFeature, loading } = useModuleAccess();

  if (loading) return null;
  if (!hasFeature(feature)) return <>{fallback}</>;

  return <>{children}</>;
}

interface EditGuardProps {
  feature: string;
  children: ReactNode;
  fallback?: ReactNode;
}

/**
 * EditGuard — only renders for users with "edit" access (not "view-only").
 * Useful for hiding edit buttons, "New" buttons, etc.
 */
export function EditGuard({ feature, children, fallback = null }: EditGuardProps) {
  const { canEdit, loading } = useModuleAccess();

  if (loading) return null;
  if (!canEdit(feature)) return <>{fallback}</>;

  return <>{children}</>;
}
